import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {Label} from '@/components/ui/label/Label'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectTrigger,
    SelectVirtualList
} from '@/components/ui/select-atoms/SelectAtoms'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {useTranslation} from '@/translations/i18n'
import {ComboboxProvider, SelectProvider} from '@ariakit/react'
import {forwardRef, useRef, useState} from 'react'
import {UserByPatient} from '@/features/users/types'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {useUsersByPatientSearchableList} from '../../hooks/useUsersByPatientSearchableList'

interface InputSelectUserByPatientProps {
    patientId: string
    disabled?: boolean
    label?: string
    value: UserByPatient | UserByPatient[] | null
    onChange: (value: UserByPatient | UserByPatient[] | null) => void
    placeholder?: string
    errorMessage?: string
    onClear?: () => void
}

export const InputSelectUserByPatient = forwardRef<HTMLSelectElement, InputSelectUserByPatientProps>(
    ({patientId, disabled, label, onChange, errorMessage, placeholder, onClear, value}, ref) => {
        const {t} = useTranslation()
        const [isQueryEnabled, setIsQueryEnabled] = useState(false)
        const inputUserRef = useRef<HTMLInputElement>(null)
        const {users, isLoading, isError, hasNextPage, fetchNextPage, onSearch, onResetSearch} =
            useUsersByPatientSearchableList({
                patientId,
                enabled: !disabled && isQueryEnabled
            })

        const valueId = Array.isArray(value) ? value.map(user => user.id) : value?.id
        const valueFullName = Array.isArray(value) ? '' : value?.fullName

        return (
            <ComboboxProvider setValue={onSearch} resetValueOnHide>
                <SelectProvider
                    value={valueId ?? ''}
                    setValue={(newValue: string) => onChange(users.find(user => user && user.id == newValue) ?? null)}
                    setOpen={(isOpen: boolean) => {
                        onResetSearch(inputUserRef)
                        setIsQueryEnabled(isOpen)
                    }}
                >
                    <Flexbox direction="column" gap={1.5} fullWidth>
                        {label && <Label>{label}</Label>}
                        <SelectTrigger
                            ref={ref}
                            placeholder={placeholder}
                            fullWidth
                            value={valueId}
                            displayValue={() => (
                                <Flexbox justify="space-between" align="center" gap={2} fullWidth>
                                    {valueFullName}
                                    {onClear && (
                                        <XCloseIcon
                                            role="button"
                                            onClick={(e: React.MouseEvent) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                onClear()
                                            }}
                                        />
                                    )}
                                </Flexbox>
                            )}
                            hasError={!!errorMessage}
                            disabled={disabled}
                        />

                        {errorMessage && (
                            <Flexbox align="center" gap={1.5}>
                                <InputStatusIcon hasError={!!errorMessage} />
                                <InputHelpText error={errorMessage} />
                            </Flexbox>
                        )}

                        <SelectComboboxPopover>
                            <SelectComboboxInput ref={inputUserRef} placeholder={t('commons:search_placeholder')} />
                            <SelectComboboxList>
                                {isLoading ? (
                                    Array.from({length: 6}, (_, index) => <Skeleton key={index} height={32} />)
                                ) : isError ? (
                                    <InputHelpText error={t('errors:default')} />
                                ) : users.length > 0 ? (
                                    <SelectVirtualList
                                        data={users}
                                        totalCount={users.length}
                                        overscan={100}
                                        endReached={() => hasNextPage && fetchNextPage()}
                                        itemContent={(itemIndex: number, item: UserByPatient) => (
                                            <SelectItem hasItemCheckbox key={itemIndex} value={item.id}>
                                                {item.fullName}
                                            </SelectItem>
                                        )}
                                    />
                                ) : (
                                    <InputHelpText helpText={t('commons:no_results')} />
                                )}
                            </SelectComboboxList>
                        </SelectComboboxPopover>
                    </Flexbox>
                </SelectProvider>
            </ComboboxProvider>
        )
    }
)
